import React, { useMemo } from "react";
import { MailingAddressResponse } from "../../../api/services/client/models";
import {
  AccordionHeaderText,
  AccordionHeader,
  MuiBox,
  MuiDoubleLabel,
} from "../../../components";
import { createClientSiteCoreKeys } from "../../createClient/siteCore/SiteCoreKeys";
import { useSiteCore } from "../../../hooks/useSiteCore";
import { ViewCompanyAddressWrapper } from "./ViewCompanyMailingAddress.style";
import {
  getValueOrBlank,
  isNotEmpty,
  isRetailClient,
} from "../../../helpers/utils";
import { useSelector } from "react-redux";
import { createClientSelector } from "../../../redux/slices/client/createClientSelector";
import { siteCoreContentLoadingSelector } from "../../../redux/slices/appState/appStateSelector";

interface ViewCompanyMailingAddressProps {
  data: MailingAddressResponse;
  addressCountryName: string;
  isExpanded?: boolean;
  onAccordionChange: (event: React.SyntheticEvent, expanded: boolean) => void;
}
export const ViewCompanyMailingAddress: React.FC<
  ViewCompanyMailingAddressProps
> = ({ data, isExpanded, onAccordionChange, addressCountryName }) => {
  const {
    fetchLabel,
    fetchComponentTitle,
    fetchDropdownListKeyValueForCountrySpecific,
  } = useSiteCore();
  const siteCoreLoading = useSelector(siteCoreContentLoadingSelector);
  const { clientMailingAddressTitle } = createClientSiteCoreKeys;
  const clientData = useSelector(createClientSelector);

  const getLabel = (key: keyof typeof createClientSiteCoreKeys) => {
    return fetchLabel(clientMailingAddressTitle, createClientSiteCoreKeys[key]);
  };

  const countrySpecificParticipantDetail = useMemo(() => {
    const stateRegionValue = fetchDropdownListKeyValueForCountrySpecific(
      createClientSiteCoreKeys.clientMailingAddressTitle,
      createClientSiteCoreKeys.stateRegionProvinceListPrefix,
      getValueOrBlank(data?.countryId),
      getValueOrBlank(data?.stateId),
    );

    return {
      stateRegionValue: isNotEmpty(stateRegionValue)
        ? stateRegionValue
        : data?.stateId,
    };
  }, [data, siteCoreLoading]);

  const getFormattedAddress = () => {
    // Address: Address1, Address2, Address3, City, state, Country, Pin code.
    const { address1, address2, address3, city, postalCode } = data;

    const addressParts = [
      address1,
      address2,
      address3,
      city,
      countrySpecificParticipantDetail.stateRegionValue,
      addressCountryName,
    ];
    const formattedAddress = addressParts.filter((part) => part).join(", ");
    if (postalCode && isNotEmpty(postalCode)) {
      return `${formattedAddress} - ${postalCode}`;
    }
    return formattedAddress;
  };
  return (
    <ViewCompanyAddressWrapper>
      <AccordionHeader
        title={
          <AccordionHeaderText
            label={
              isRetailClient(clientData.clientTypeId)
                ? getLabel("individualMailingAddressSectionHeading")
                : fetchComponentTitle(clientMailingAddressTitle)
            }
          />
        }
        isExpanded={isExpanded}
        onChange={onAccordionChange}
      >
        {data && (
          <MuiBox className="container">
            <MuiDoubleLabel
              label={getLabel("address")}
              value={getFormattedAddress()}
            />
          </MuiBox>
        )}
      </AccordionHeader>
    </ViewCompanyAddressWrapper>
  );
};
